'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React from 'react';

import { ActivitiesSection } from '@ui/sections/activities/ActivitiesSection';

import type { Activity } from '../../../../../services/activities.service';

interface ProfessionalActivitiesSectionProps {
  activities: Activity[];
  professionalName: string;
}

/**
 * Professional activities section - displays activities by the same professional
 * Data is passed from server (SSG), no client-side fetching
 */
export const ProfessionalActivitiesSection: React.FC<ProfessionalActivitiesSectionProps> = ({
  activities,
  professionalName
}) => {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('ActivitiesSection');
  const tActivityDetail = useTranslations('ActivityDetail');
  const tActivityCard = useTranslations('ActivityCard');

  // Don't render if there are no activities
  if (activities.length === 0) {
    return null;
  }

  const getActivityHref = (activityId: string) => `/${locale}/activity/${activityId}`;

  const handleActivityClick = (activityId: string) => {
    router.push(getActivityHref(activityId));
  };

  const handleSeeMoreClick = () => {
    router.push(`/search?professional=${encodeURIComponent(professionalName)}`);
  };

  return (
    <ActivitiesSection
      activities={activities}
      isLoading={false}
      isError={false}
      onActivityClick={handleActivityClick}
      getActivityHref={getActivityHref}
      onSeeMoreClick={handleSeeMoreClick}
      headingText={tActivityDetail('moreFromProfessional', { professionalName })}
      seeMoreText={t('seeMore')}
      noActivitiesText={tActivityDetail('noOtherActivitiesFromProfessional', { professionalName })}
      atHomeText={tActivityCard('atHome')}
      atVenueText={tActivityCard('atVenue')}
      atHubText={(name) => tActivityCard('atHub', { name })}
      onlineText={tActivityCard('online')}
      bookingsInYearPrefix={tActivityCard('bookingsInYearPrefix')}
      bookingsInYearMiddle={tActivityCard('bookingsInYearMiddle')}
      bookingsInYearSuffix={tActivityCard('bookingsInYearSuffix')}
      onwardsText={tActivityCard('onwards')}
      freeText={tActivityCard('free')}
      fullWidth={true}
      disableVerticalPadding={true}
      showSeeMore={false}
    />
  );
};
